import React, { useEffect, useState } from "react";
import { Upload, Trash2 } from "lucide-react";
import { UrduInput } from "../../../Components/Shared/UrduInput";
import {
  ImageSeoFields,
  appendImageSeoToFormData,
} from "../../../Components/Shared/ImageSeoFields";

const REQUIRED_WIDTH = 450;
const REQUIRED_HEIGHT = 450;

const checkImageDimension = (file) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = function () {
      if (this.width === REQUIRED_WIDTH && this.height === REQUIRED_HEIGHT) {
        resolve(true);
      } else {
        resolve(false);
      }
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
};

function ServicesCategoryModal({
  modalOpen,
  toggleModal,
  category,
  handleSaveCategory,
  isSubmitting,
}) {
  const [categoryData, setCategoryData] = useState({
    name: "",
    name_ur: "",
    description: "",
    description_ur: "",
    custom_url: "",
    image: null,
    imagePreview: null,
    image_alt: "",
    image_title: "",
  });
  const [error, setError] = useState("");
  const [customUrlError, setCustomUrlError] = useState("");

  useEffect(() => {
    if (category) {
      const baseUrl = (process.env.REACT_APP_BASE_URL || "").replace(/\/$/, "");
      const imagePreview = category.image
        ? category.image.startsWith("http")
          ? category.image
          : `${baseUrl}${category.image.startsWith("/") ? "" : "/"}${category.image}`
        : null;
      setCategoryData({
        name: category.name || "",
        name_ur: category.name_ur || "",
        description: category.description || "",
        description_ur: category.description_ur || "",
        custom_url: (category.custom_url || "").replace(/\/$/, ""),
        image: null,
        imagePreview: imagePreview,
        image_alt: category.image_alt || "",
        image_title: category.image_title || "",
      });
    } else {
      setCategoryData({
        name: "",
        name_ur: "",
        description: "",
        description_ur: "",
        custom_url: "",
        image: null,
        imagePreview: null,
        image_alt: "",
        image_title: "",
      });
    }
    setError("");
    setCustomUrlError("");
  }, [category]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "custom_url") {
      setCustomUrlError("");
      if (value.startsWith("/")) {
        setCustomUrlError("Custom URL should not start with '/'");
      }
    }
    setCategoryData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setError("");
      const isValid = await checkImageDimension(file);
      if (!isValid) {
        setError(
          `Image dimensions must be exactly ${REQUIRED_WIDTH}x${REQUIRED_HEIGHT} pixels.`,
        );
        // Reset the file input value so the same file could be picked after correction
        e.target.value = "";
        return;
      }

      setCategoryData((prev) => ({
        ...prev,
        image: file,
        imagePreview: URL.createObjectURL(file),
      }));
    }
  };

  const removeImage = () => {
    if (
      categoryData.imagePreview &&
      categoryData.imagePreview.startsWith("blob:")
    ) {
      URL.revokeObjectURL(categoryData.imagePreview);
    }
    setCategoryData((prev) => ({
      ...prev,
      image: null,
      imagePreview: null,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    setError("");
    setCustomUrlError("");
    if (!categoryData.name.trim()) {
      setError("Category name is required.");
      return;
    }

    if (!categoryData.custom_url.trim()) {
      setCustomUrlError("Custom URL is required.");
      return;
    }

    if (categoryData.custom_url.trim().startsWith("/")) {
      setCustomUrlError("Custom URL should not start with '/'");
      return;
    }

    if (!categoryData.description.trim()) {
      setError("Description is required.");
      return;
    }

    if (!categoryData.image && !categoryData.imagePreview) {
      setError("Image is required.");
      return;
    }

    const formData = new FormData();
    formData.append("name", categoryData.name.trim());
    formData.append("custom_url", categoryData.custom_url.trim());
    if (categoryData.name_ur) {
      formData.append("name_ur", categoryData.name_ur.trim());
    }
    if (categoryData.description) {
      formData.append("description", categoryData.description.trim());
    }
    if (categoryData.description_ur) {
      formData.append("description_ur", categoryData.description_ur.trim());
    }
    if (categoryData.image) {
      formData.append("image", categoryData.image);
    }
    appendImageSeoToFormData(
      formData,
      categoryData.image_alt,
      categoryData.image_title,
    );

    handleSaveCategory(formData, category ? category.id : null);
  };

  const handleClose = () => {
    setError("");
    setCustomUrlError("");
    toggleModal();
  };

  if (!modalOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white p-6 rounded-lg w-[560px] max-w-[95vw] max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold">
            {category ? "Edit Service Category" : "Add Service Category"}
          </h2>
        </div>
        <form onSubmit={handleSubmit} className="p-4">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={categoryData.name}
              onChange={handleInputChange}
              className="w-full border border-gray-300 rounded-md p-2"
              required
            />
            <UrduInput
              label="نام"
              name="name_ur"
              value={categoryData.name_ur}
              onChange={handleInputChange}
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Custom URL <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="custom_url"
              value={categoryData.custom_url}
              onChange={handleInputChange}
              placeholder="e.g. construction-services"
              className="w-full border border-gray-300 rounded-md p-2"
              required
            />
            {customUrlError && (
              <p className="text-red-500 text-sm mt-1">{customUrlError}</p>
            )}
            <p className="text-[10px] text-gray-400 mt-1">
              Do not start with "/". Backend will slugify and ensure uniqueness.
            </p>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="description"
              value={categoryData.description}
              onChange={handleInputChange}
              maxLength={100}
              placeholder="Brief description (max 100 characters)"
              className="w-full border border-gray-300 rounded-md p-2"
              required
            />
            <div className="flex justify-between mt-1">
              <p className="text-[10px] text-gray-400">
                {categoryData.description.length}/100 characters
              </p>
            </div>
            <UrduInput
              label="تفصیل"
              name="description_ur"
              value={categoryData.description_ur}
              onChange={handleInputChange}
              type="textarea"
              rows={2}
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Image <span className="text-red-500">*</span>
            </label>
            <div className="border border-dashed border-gray-300 p-4 rounded-md mb-4 text-center">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="hidden"
                id="category-image"
              />

              {!categoryData.imagePreview && (
                <label
                  htmlFor="category-image"
                  className="flex flex-col items-center justify-center cursor-pointer"
                >
                  <Upload size={24} className="text-gray-500 mb-2" />
                  <span className="text-sm text-gray-500">
                    Click to upload image
                  </span>
                  <span className="text-[10px] text-gray-400 mt-1">
                    Required size: {REQUIRED_WIDTH}x{REQUIRED_HEIGHT} pixels
                  </span>
                </label>
              )}
              {(categoryData.imagePreview || categoryData.image) && (
                <div className="mt-2 text-center">
                  <div className="relative inline-block">
                    <img
                      src={categoryData.imagePreview}
                      alt="Preview"
                      className="h-32 object-cover rounded border"
                    />
                    <button
                      type="button"
                      onClick={removeImage}
                      className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1 -mt-2 -mr-2"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                  <p className="text-[10px] text-gray-400 mt-2">
                    Click "Trash" to upload a different image
                  </p>
                </div>
              )}
              <ImageSeoFields
                imageAlt={categoryData.image_alt}
                imageTitle={categoryData.image_title}
                onAltChange={(value) =>
                  setCategoryData((prev) => ({ ...prev, image_alt: value }))
                }
                onTitleChange={(value) =>
                  setCategoryData((prev) => ({ ...prev, image_title: value }))
                }
                className="mt-3 text-left"
              />
            </div>
            {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
          </div>

          <div className="flex justify-end gap-3 border-t border-gray-200 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="btn btn-reset-filter justify-center"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`btn justify-center ${
                category ? "btn-edit text-white" : "btn-primary"
              } ${isSubmitting ? "opacity-75 cursor-not-allowed" : ""}`}
              disabled={isSubmitting}
            >
              {isSubmitting
                ? "Processing..."
                : category
                  ? "Update"
                  : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ServicesCategoryModal;
